import { useState } from 'react';
import { Check, Plus, Building2, User, Settings, HelpCircle, Mail, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { AppModal } from '@/components/ui/modal';
import { useProfile } from '@/contexts/profile';
import { useAuth } from '@/contexts/auth';
import { ProfileForm } from '@/components/profile/ProfileForm';
import { FeedbackForm } from '@/components/forms/FeedbackForm';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const UserDropdown = () => {
  const { profiles, activeProfile, setActiveProfile } = useProfile();
  const { signOut } = useAuth();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const navigate = useNavigate();

  const handleProfileChange = (profile: any) => {
    setActiveProfile(profile);
    setIsPopoverOpen(false);
  };

  const handleMenuAction = async (action: string) => {
    setIsPopoverOpen(false);
    if (action === 'settings') {
      navigate('/configuracoes');
    } else if (action === 'help') {
      navigate('/ajuda-e-suporte');
    } else if (action === 'logout') {
      try {
        await signOut();
        navigate('/');
        toast.success('Logout realizado com sucesso');
      } catch (error) {
        console.error('Erro ao fazer logout:', error);
        toast.error('Erro ao fazer logout');
      }
    } else if (action === 'feedback') {
      setIsFeedbackOpen(true);
    } else {
      console.log(`Action: ${action}`);
    }
  };

  const getProfileIcon = (type: 'personal' | 'business') => {
    return type === 'personal' ? <User className="h-4 w-4" /> : <Building2 className="h-4 w-4" />;
  };

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <Button 
            variant="outline" 
            className="flex items-center gap-2 h-10 px-3 bg-background border-border/50 hover:bg-background"
            aria-label={`Menu do perfil: ${activeProfile?.name || 'Usuário'}`}
          >
            {activeProfile?.type === 'business' ? (
              <Building2 className="h-4 w-4" aria-hidden="true" />
            ) : (
              <User className="h-4 w-4" aria-hidden="true" />
            )}
            <span className="hidden sm:inline">{activeProfile?.name || 'Usuário'}</span>
            <ChevronDown className="h-4 w-4" aria-hidden="true" />
          </Button>
        </PopoverTrigger>
        
        <PopoverContent align="end" className="w-64 bg-background border-border/50 shadow-lg p-0" sideOffset={8}>
          <div className="p-4">
            <div className="font-medium text-sm text-foreground mb-2">
              {activeProfile?.name || 'Usuário'}
            </div>
            
            <div className="space-y-1">
              {profiles.map(profile => (
                <button 
                  key={profile.id} 
                  onClick={() => handleProfileChange(profile)} 
                  className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left"
                >
                  {getProfileIcon(profile.type)}
                  <span className="flex-1 text-sm">{profile.name}</span>
                  <Badge variant="secondary" className="text-xs" style={{
                    backgroundColor: profile.color + '20',
                    color: profile.color
                  }}>
                    {profile.type === 'personal' ? 'Pessoal' : 'Empresa'}
                  </Badge>
                  {profile.id === activeProfile?.id && <Check className="h-4 w-4 text-primary" />}
                </button>
              ))}
              
              <button 
                onClick={() => {
                  setIsCreateOpen(true);
                  setIsPopoverOpen(false);
                }} 
                className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left text-primary"
              >
                <Plus className="h-4 w-4" />
                <span className="text-sm">Adicionar perfil</span>
              </button>
            </div>
          </div>

          <Separator className="bg-border/50" />
          
          <div className="p-2">
            <button onClick={() => handleMenuAction('settings')} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left">
              <Settings className="h-4 w-4" />
              <span className="text-sm">Configurações</span>
            </button>
            
            <button onClick={() => handleMenuAction('help')} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left">
              <HelpCircle className="h-4 w-4" />
              <span className="text-sm">Ajuda e Suporte</span>
            </button>
            
            <button onClick={() => handleMenuAction('feedback')} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left">
              <Mail className="h-4 w-4" />
              <span className="text-sm">Feedback e Sugestões</span>
            </button>
          </div>
          
          <Separator className="bg-border/50" />
          
          <div className="p-2">
            <button onClick={() => handleMenuAction('logout')} className="w-full flex items-center space-x-3 p-2 rounded-lg hover:bg-accent/50 transition-colors text-left text-destructive">
              <LogOut className="h-4 w-4" />
              <span className="text-sm">Sair</span>
            </button>
          </div>
        </PopoverContent>
      </Popover>

      {/* Create Profile Modal */}
      <AppModal 
        open={isCreateOpen} 
        onOpenChange={setIsCreateOpen}
        variant="form"
        size="md"
      >
        <ProfileForm onSuccess={() => setIsCreateOpen(false)} onClose={() => setIsCreateOpen(false)} />
      </AppModal>

      {/* Feedback Modal */}
      <AppModal 
        open={isFeedbackOpen} 
        onOpenChange={setIsFeedbackOpen}
        variant="form"
        size="lg"
      >
        <FeedbackForm onClose={() => setIsFeedbackOpen(false)} />
      </AppModal>
    </>
  );
};
